import Typography, { ETypo, ETypoColor } from "@Front/Components/DesignSystem/Typography";
import Dot from "@Front/Components/Layouts/Legal/LegalInformations/elements/Dot";
import LinkInline from "../../elements/LinkInline";
import { useDeploymentLineLegalPublisher } from "@Front/Services/DeploymentLineLegalPublisher/DeploymentLineLegalPublisherContext";
import {
	RightToAccessClosing,
	RightToAccessDotList,
	RightToAccessHeading,
	RightToAccessIntro,
	RightToInformationIntro,
} from "./RightToInformationAndAccessParts";
import type { DeploymentLineLegalPublisher } from "le-coffre-resources/dist/deploymentLineLegalPublisher";

function UserRightsExerciseFeesParagraph(): React.JSX.Element {
	return (
		<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
			Par principe, vous pouvez exercer sans frais l&apos;ensemble de vos droits. Cependant en matière de droit d&apos;accès, il pourra vous
			être demandé le paiement de frais raisonnables basés sur les coûts administratifs pour toute copie des données que vous
			demanderez.
		</Typography>
	);
}

function UserRightsExerciseInformationParagraph(props: { pub: DeploymentLineLegalPublisher }): React.JSX.Element {
	const { pub } = props;
	return (
		<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
			Concernant le droit d&apos;information, {pub.publisherLegalName} n&apos;aura pas l&apos;obligation d&apos;y donner suite lorsque vous disposez déjà des
			informations dont vous sollicitez la communication.
		</Typography>
	);
}

/**
 * User Rights Exercise Modalities Section
 */
export function UserRightsExerciseModalities(): React.JSX.Element {
	const pub = useDeploymentLineLegalPublisher();
	return (
		<section>
			<Typography typo={ETypo.TITLE_H6} color={ETypoColor.TEXT_ACCENT}>
				7. Les droits qui vous sont reconnus
			</Typography>
			<Typography typo={ETypo.TEXT_LG_SEMIBOLD} color={ETypoColor.TEXT_SECONDARY}>
				Modalités d&apos;exercice de vos droits
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Vous pouvez exercer vos droits par voie électronique à l&apos;adresse suivante ({pub.contactEmail}) ou par courrier postal à
				l&apos;adresse {pub.publisherLegalName} - {pub.postalAddressLine}, en justifiant de votre identité.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Pour ce faire, vous devez indiquer clairement vos nom(s) et prénom(s), l&apos;adresse à laquelle vous souhaitez que la réponse
				vous soit envoyée et y joindre la photocopie d&apos;un titre d&apos;identité portant votre signature.
			</Typography>
			<UserRightsExerciseFeesParagraph />
			<UserRightsExerciseInformationParagraph pub={pub} />
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				{pub.publisherLegalName} vous informera si elle ne peut donner suite à vos demandes.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				{pub.publisherLegalName} tient à vous informer que le non-renseignement ou la modification de vos données sont susceptibles d&apos;avoir des
				conséquences dans le traitement de certaines demandes dans le cadre de l&apos;exécution des relations contractuelles et que votre
				demande au titre de l&apos;exercice de vos droits sera conservée à des fins de suivi.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				L&apos;ensemble des droits dont vous bénéficiez est détaillé ci-dessous :
			</Typography>
		</section>
	);
}

/**
 * Right to Information and Access Section
 */
export function RightToInformationAndAccess(): React.JSX.Element {
	return (
		<section>
			<RightToInformationIntro />
			<RightToAccessHeading />
			<RightToAccessIntro />
			<RightToAccessDotList />
			<RightToAccessClosing />
		</section>
	);
}

/**
 * Right to Erasure Section
 */
export function RightToErasure(): React.JSX.Element {
	return (
		<section>
			<Typography typo={ETypo.TEXT_LG_SEMIBOLD} color={ETypoColor.TEXT_SECONDARY}>
				Droit à l&apos;effacement de vos données
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Vous pouvez nous demander l&apos;effacement de vos données personnelles lorsque l&apos;un des motifs suivants s&apos;applique :
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				<Dot>
					les données personnelles ne sont plus nécessaires au regard des finalités pour lesquelles elles ont été collectées ou
					traitées d&apos;une autre manière ;
				</Dot>
				<Dot>vous retirez le consentement préalablement donné ;</Dot>
				<Dot>
					vous vous opposez au traitement de vos données personnelles lorsqu&apos;il n&apos;existe pas de motif légal audit traitement ;
				</Dot>
				<Dot>
					le traitement de données personnelles n&apos;est pas conforme aux dispositions de la législation et de la réglementation
					applicable ;
				</Dot>
				<Dot>
					vos données personnelles ont été collectées dans le cadre de l&apos;offre de services de la société de l&apos;information aux enfants
					âgés de moins de 16 ans.
				</Dot>
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Néanmoins, l&apos;exercice de ce droit ne sera pas possible lorsque la conservation de vos données personnelles est nécessaire au
				regard de la législation ou de la réglementation et notamment par exemple pour la constatation, l&apos;exercice ou la défense de
				droits en justice.
			</Typography>
		</section>
	);
}

/**
 * Right to Limitation Section
 */
export function RightToLimitation(): React.JSX.Element {
	return (
		<section>
			<Typography typo={ETypo.TEXT_LG_SEMIBOLD} color={ETypoColor.TEXT_SECONDARY}>
				Droit à la limitation des traitements de données
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Vous pouvez demander la limitation du traitement de vos données personnelles dans les cas prévus par la législation et la
				réglementation.
			</Typography>
		</section>
	);
}

/**
 * Right to Objection Section
 */
export function RightToObjection(): React.JSX.Element {
	return (
		<section>
			<Typography typo={ETypo.TEXT_LG_SEMIBOLD} color={ETypoColor.TEXT_SECONDARY}>
				Droit de vous opposer aux traitements de données
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Vous avez le droit de vous opposer à un traitement de données personnelles vous concernant lorsque le traitement est fondé
				sur l&apos;intérêt légitime du responsable du traitement.
			</Typography>
		</section>
	);
}

/**
 * Right to Portability Section
 */
export function RightToPortability(): React.JSX.Element {
	return (
		<section>
			<Typography typo={ETypo.TEXT_LG_SEMIBOLD} color={ETypoColor.TEXT_SECONDARY}>
				Droit à la portabilité de vos données
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Vous disposez du droit à la portabilité de vos données personnelles, lorsque :
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				<Dot>
					le traitement est fondé sur le consentement en application de l&apos;article 6, paragraphe 1, point a), ou de l&apos;article 9,
					paragraphe 2, point a), ou sur un contrat en application de l&apos;article 6, paragraphe 1, point b) du RGPD ;
				</Dot>
				<Dot>le traitement est effectué à l&apos;aide de procédés automatisés.</Dot>
			</Typography>
		</section>
	);
}

/**
 * Right to Withdraw Consent Section
 */
export function RightToWithdrawConsent(): React.JSX.Element {
	return (
		<section>
			<Typography typo={ETypo.TEXT_LG_SEMIBOLD} color={ETypoColor.TEXT_SECONDARY}>
				Droit de retirer votre consentement
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Lorsque les traitements de données que nous mettons en œuvre sont fondés sur votre consentement, vous pouvez le retirer à
				n&apos;importe quel moment. Nous cessons alors de traiter vos données à caractère personnel sans que les opérations antérieures
				pour lesquelles vous aviez consenti ne soient remises en cause.
			</Typography>
		</section>
	);
}

/**
 * Right to Lodge Complaint Section
 */
export function RightToLodgeComplaint(): React.JSX.Element {
	const pub = useDeploymentLineLegalPublisher();
	return (
		<section>
			<Typography typo={ETypo.TEXT_LG_SEMIBOLD} color={ETypoColor.TEXT_SECONDARY}>
				Droit d&apos;introduire un recours
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Si vous considérez que {pub.publisherLegalName} ne respecte pas ses obligations au regard de vos données à caractère personnel, vous
				pouvez adresser une plainte ou une demande auprès de l&apos;autorité compétente. En France, l&apos;autorité compétente est la Cnil à
				laquelle vous pouvez adresser une demande par voie électronique en cliquant sur le lien suivant{" "}
				<LinkInline text="https://www.cnil.fr/fr/plaintes/internet" href="https://www.cnil.fr/fr/plaintes/internet" />
			</Typography>
		</section>
	);
}

/**
 * Right to Post-Mortem Directives Section
 */
export function RightToPostMortemDirectives(): React.JSX.Element {
	return (
		<section>
			<Typography typo={ETypo.TEXT_LG_SEMIBOLD} color={ETypoColor.TEXT_SECONDARY}>
				Droit de définir des directives post-mortem
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Vous avez la possibilité de définir des directives relatives à la conservation, à l&apos;effacement et à la communication de vos
				données personnelles après votre décès et ce auprès d&apos;un tiers de confiance, certifié et chargé de faire respecter la
				volonté du défunt, conformément aux exigences du cadre juridique applicable.
			</Typography>
		</section>
	);
}
